// GENERATED by scripts/gen.mjs from ../schema — do not edit by hand. Re-run: pnpm --filter @glyph/protocol-ts gen
export interface Settings {
  config_version: number;
  deepgram: DeepgramSettings;
  audio: AudioSettings;
  display: DisplaySettings;
  outputs: OutputsSettings;
  network: NetworkSettings;
  device: DeviceSettings;
  auth: AuthSettings;
  system: SystemSettings;
  record: RecordSettings;
  wordlib: WordLibSettings;
  cloud: CloudSettings;
  partner: PartnerSettings;
  update: UpdateSettings;
  translation: TranslationSettings;
  setup: SetupSettings;
}
/**
 * Deepgram streaming parameters. No api_key: credentials are brokered by the cloud.
 */
export interface DeepgramSettings {
  model: string;
  language: string;
  endpointing: number;
  utterance_end_ms: number;
  interim_results: boolean;
  punctuate: boolean;
  smart_format: boolean;
  numerals: boolean;
  filler_words: boolean;
  profanity_filter: boolean;
  redact: string[];
  diarize: boolean;
  vad_events: boolean;
}
export interface AudioSettings {
  device: string;
  sample_rate: number;
  channels: number;
  /**
   * 1-based capture channel sent to ASR
   */
  program_channel: number;
  /**
   * 1-based capture channel decoded as LTC; 0 = off
   */
  ltc_channel: number;
  silence_dbfs: number;
  /**
   * raise audio_silent when program audio stays below silence_dbfs while captioning; off by default because sessions go quiet on purpose (workshops, breaks)
   */
  silence_alert: boolean;
  /**
   * seconds of silence before audio_silent fires
   */
  silence_alert_s: number;
  ring_seconds: number;
}
/**
 * The renderer Style (PLAN > Renderer > Style schema). Persisted as-is by glyph-render.
 */
export interface DisplaySettings {
  font_family: string;
  font_file: string;
  font_size_px: number;
  font_weight: number;
  font_style: 'normal' | 'italic';
  letter_spacing_px: number;
  line_height: number;
  text_transform: 'none' | 'upper' | 'lower' | 'capitalize';
  text_color: string;
  text_align: 'left' | 'center' | 'right';
  max_lines: number;
  width_pct: number;
  v_align: 'top' | 'middle' | 'bottom';
  h_align: 'left' | 'center' | 'right';
  offset_x_pct: number;
  offset_y_pct: number;
  safe_area_pct: number;
  outline: OutlineStyle;
  text_shadow: TextShadowStyle;
  box: BoxStyle;
  box_shadow: BoxShadowStyle;
  mode: 'rollup' | 'popon' | 'painton';
  scroll_duration_ms: number;
  word_reveal_ms: number;
  auto_clear_ms: number;
  popon_show_interim: boolean;
  partial_style: PartialStyle;
  key_color: string;
  top_fade_px: number;
}
export interface OutlineStyle {
  enabled: boolean;
  color: string;
  width: number;
}
export interface TextShadowStyle {
  enabled: boolean;
  color: string;
  blur: number;
  offset_x: number;
  offset_y: number;
  opacity: number;
}
export interface BoxStyle {
  enabled: boolean;
  per_line: boolean;
  color: string;
  opacity: number;
  padding_x: number;
  padding_y: number;
  radius: number;
}
export interface BoxShadowStyle {
  enabled: boolean;
  color: string;
  blur: number;
  opacity: number;
  offset_x: number;
  offset_y: number;
}
export interface PartialStyle {
  italic: boolean;
  opacity: number;
}
export interface OutputsSettings {
  policy: 'mirror' | 'independent';
  /**
   * connector name -> layout id
   */
  assignments: {
    [k: string]: string;
  };
  /**
   * e.g. "1920x1080@59.94"; blank = adopt kernel mode
   */
  mode_override: string;
  keep_signal_on_loss: boolean;
  auto_detect: boolean;
}
export interface NetworkSettings {
  ap_enabled: boolean;
  /**
   * "auto" = last 4 of serial; SSID is derived as Glyph-<device.name>-<suffix>
   */
  ap_ssid_suffix: string;
  ap_password: string;
  ap_open: boolean;
  ap_hidden: boolean;
  ap_band: '2.4' | '5';
  ap_channel: number;
  ap_ip: string;
  wifi_enabled: boolean;
  wifi_band: 'auto' | '2.4' | '5';
  eth_method: 'auto' | 'manual';
  eth_address: string;
  eth_gateway: string;
  eth_dns: string;
  hostname: string;
  lte_enabled: boolean;
  lte_apn: string;
}
export interface DeviceSettings {
  /**
   * Free-form display name (OLED, fleet, mDNS); the hotspot SSID uses its slug (letters, digits, hyphens)
   */
  name: string;
}
/**
 * Two local accounts for the LAN / hotspot UI: FDG Admin (everything) and Tech (day-to-day
 * operation: live, captions, speech, recordings, outputs, Wi-Fi). Empty admin password = no
 * login required (bench / first boot, the wizard sets it). `pin` is the pre-account field and is
 * migrated into admin_password on load.
 */
export interface AuthSettings {
  /**
   * FDG Admin account (username fdgadmin) · empty = no login required
   */
  admin_password: string;
  /**
   * Tech account username
   */
  tech_username: string;
  /**
   * Tech account · empty = account disabled
   */
  tech_password: string;
  /**
   * deprecated: migrated into admin_password
   */
  pin: string;
  /**
   * bearer token for the local API (blank = disabled)
   */
  api_token: string;
}
export interface SystemSettings {
  oled_enabled: boolean;
  oled_show_ap_pass: boolean;
  oled_cloud_mode: 'auto' | 'classic' | 'stats';
}
export interface RecordSettings {
  tc_source: 'off' | 'freerun' | 'system' | 'ltc' | 'atem' | 'hyperdeck';
  tc_host: string;
  tc_framerate: 23.976 | 24 | 25 | 29.97 | 30 | 50 | 59.94 | 60;
  tc_start: string;
  tc_offset_frames: number;
  record_audio: boolean;
  audio_format: 'flac' | 'wav';
  always_on: boolean;
  save_dir: string;
  drive_uuid: string;
  drive_subpath: string;
  drive_fallback_to_internal: boolean;
  /**
   * 0 = off
   */
  retention_days: number;
  min_free_gb: number;
  store_raw: boolean;
  export_chars_per_line: number;
  export_zero: 'session' | 'tc_hour' | 'custom';
  export_zero_tc: string;
}
export interface WordLibSettings {
  active_library_id: string;
  blocked_mode: 'replace' | 'blank' | 'drop';
  vulgarity_filter: boolean;
}
export interface CloudSettings {
  enabled: boolean;
  url: string;
  auto_enrol: boolean;
  relay_captions: boolean;
  heartbeat_s: number;
  upload_policy: 'metadata_only' | 'on_request' | 'auto_on_stop' | 'auto_on_stop_wired_only';
}
export interface PartnerSettings {
  enabled: boolean;
  role: 'primary' | 'backup';
  follow: 'off' | 'on' | 'auto';
  peer_host: string;
}
export interface UpdateSettings {
  channel: 'stable' | 'beta' | 'canary';
  /**
   * When true the device refuses every update offer or apply, cloud or local, until unlocked. Off by default and hidden in the consoles (2026-10-21); the cloud unlocks units that still report locked.
   */
  lock: boolean;
}
export interface TranslationSettings {
  enabled: boolean;
}
export interface SetupSettings {
  completed: boolean;
}

// ---- protocol constants (from x-* schema fields) --------------------------------------------------
export type DeviceId = string;
/** Secret fields come back as this sentinel; a UI must never echo it in a PATCH (the server ignores it anyway). */
export const MASKED_SECRET = "••••••••";
export const SECRET_KEYS: readonly string[] = ["admin_password","ap_password","api_token","pin","tech_password"];
export type SettingsSection = 'deepgram' | 'audio' | 'display' | 'outputs' | 'network' | 'device' | 'auth' | 'system' | 'record' | 'wordlib' | 'cloud' | 'partner' | 'update' | 'translation' | 'setup';
export const SETTINGS_SECTIONS: readonly SettingsSection[] = ["deepgram","audio","display","outputs","network","device","auth","system","record","wordlib","cloud","partner","update","translation","setup"];
/** Per-section capability needed for settings.patch (mirrors glyph_protocol.commands.SECTION_CAPABILITY). */
export const SECTION_CAPABILITY: Record<SettingsSection, string> = {
  "deepgram": "settings.write",
  "audio": "settings.write",
  "display": "settings.write",
  "outputs": "settings.write",
  "record": "settings.write",
  "wordlib": "settings.write",
  "translation": "settings.write",
  "network": "network.write",
  "device": "device.rename",
  "system": "settings.admin",
  "cloud": "settings.admin",
  "partner": "partner.write",
  "update": "settings.admin",
  "setup": "settings.admin",
  "auth": "auth.write"
};
export type DeepPartial<T> = T extends (infer U)[] ? U[] : T extends object ? { [K in keyof T]?: DeepPartial<T[K]> } : T;
/** settings.patch body: any subset of sections, each deep-merged key by key (outputs.assignments is replaced). */
export type SettingsPatch = { [K in SettingsSection]?: DeepPartial<Settings[K]> };
